import type { FC } from "react";
import { useEffect } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { GoogleAPIProvider } from "@jti/fe-clients";
import { useRegions } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { selectPostCodeConfig } from "@jti/store/selectors/selectPostCodeConfig";
import { AddressFormProvider } from "@jti/ui";

import { OrderAttributes } from "@checkout/components/OrderAttributes";
import { SHIPPING_FORM_FIELDS } from "@checkout/forms/ShippingForm/constants";
import { useAddressRegionMapping } from "@checkout/forms/ShippingForm/hooks/useAddressRegionMapping";

import { useShippingFormContext } from "../../../hooks/useShippingFormContext";

const BillingAddressEditForm: FC = () => {
  const locale = useLocale();
  const postCodeConfig = useSelector(selectPostCodeConfig);
  const {
    locationServiceAutocompleteProvider,
    addressFormVariant,
    ISO_COUNTRY_CODE,
    isSingleFieldAddressLookup,
    GOOGLE_MAPS_API,
    isStreetFieldCombined,
    ksaProvinces,
  } = useConfig();
  const formContext = useShippingFormContext();
  const { clearErrors, formState } = formContext;
  const { errors } = formState;
  const billingAddress = formContext.watch(SHIPPING_FORM_FIELDS.billingAddress);
  const billingAddressErrorType = errors.root?.billingAddress?.type;

  const hasRegionField = addressFormVariant === "with_regions" || addressFormVariant === "with_regions_reversed";
  const { regions } = useRegions(ISO_COUNTRY_CODE, hasRegionField);

  useAddressRegionMapping(formContext, SHIPPING_FORM_FIELDS.billingAddress, regions);

  useEffect(
    function clearServerErrors() {
      if (billingAddressErrorType === "server") {
        clearErrors("root.billingAddress");
      }
    },
    [clearErrors, billingAddress?.postcode, billingAddressErrorType]
  );

  return (
    <>
      <OrderAttributes target={"billingaddress_before"} />
      <GoogleAPIProvider
        apiKey={GOOGLE_MAPS_API}
        options={{
          version: "weekly",
          libraries: ["geometry", "places"],
          language: locale,
          region: ISO_COUNTRY_CODE,
        }}
      >
        <AddressFormProvider
          locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
          formType={addressFormVariant}
          fieldName={(name: string) => `billingAddress.${name}`}
          postCodeConfig={postCodeConfig}
          regions={regions}
          isSingleFieldAddressLookup={isSingleFieldAddressLookup}
          isStreetFieldCombined={isStreetFieldCombined}
          ksaProvinces={ksaProvinces}
        />
      </GoogleAPIProvider>
      <OrderAttributes target={"billingaddress_after"} />
    </>
  );
};

export default BillingAddressEditForm;
